import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Alert, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuthStore, type LoginMethod } from '../../stores/authStore';
import { useAdminAuthStore } from '../../stores/adminAuthStore';
import { authService } from '../../services/authService';
import { TermsModal } from './TermsModal';
import { TestVersionNotice } from '../common/TestVersionNotice';
import { useI18n } from '../../i18n/useI18n';
import type { ClientShell } from '../../constants/clientShell';

import './AuthStyles.css';

interface LoginFormProps {
  initialLoginMethod?: LoginMethod;
  onSuccess?: (method?: LoginMethod, shell?: ClientShell) => void;
  onSwitchToRegister?: () => void;
  onForgotPassword?: () => void;
}

const LOGIN_METHODS = [
  { key: 'username', labelKey: 'auth.usernameMethod' },
  { key: 'email', labelKey: 'auth.emailMethod' },
  { key: 'admin', labelKey: 'auth.adminMethod' },
] as const satisfies ReadonlyArray<{ key: LoginMethod; labelKey: string }>;

const SEND_CODE_COOLDOWN_SEC = 60;

export const LoginForm: React.FC<LoginFormProps> = ({
  initialLoginMethod = 'username',
  onSuccess,
  onSwitchToRegister,
  onForgotPassword,
}) => {
  const [form] = Form.useForm();
  const { t } = useI18n();
  const { login, loginByEmail, isLoading, error, clearError } = useAuthStore();
  const {
    login: loginAsAdmin,
    isLoading: isAdminLoading,
    error: adminError,
    clearError: clearAdminError,
  } = useAdminAuthStore();
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(initialLoginMethod);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [privacyModalVisible, setPrivacyModalVisible] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [codeInfoMessage, setCodeInfoMessage] = useState<string | null>(null);
  const [sendCodeError, setSendCodeError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const loginTargetRef = useRef<ClientShell | undefined>(undefined);

  useEffect(() => {
    setLoginMethod(initialLoginMethod);
  }, [initialLoginMethod]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const resetCodeState = () => {
    setCodeInfoMessage(null);
    setSendCodeError(null);
    setCooldown(0);
    form.resetFields(['code']);
  };

  const handleMethodChange = (method: LoginMethod) => {
    setLoginMethod(method);
    clearError();
    clearAdminError();
    resetCodeState();
    form.resetFields(['username', 'email', 'password', 'code']);
  };

  const sendVerificationCode = async () => {
    setSendCodeError(null);
    setCodeInfoMessage(null);
    clearError();

    try {
      await form.validateFields(['email']);
      const values = form.getFieldsValue();
      setSendCodeLoading(true);

      const message = await authService.sendLoginEmailCode(values.email);
      setCodeInfoMessage(message);
      setCooldown(SEND_CODE_COOLDOWN_SEC);
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      setSendCodeError(err instanceof Error ? err.message : t('auth.sendCodeFailed'));
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleSubmit = async (values: {
    username?: string;
    email?: string;
    password?: string;
    code?: string;
  }) => {
    clearError();
    clearAdminError();
    setSendCodeError(null);

    try {
      if (loginMethod === 'admin') {
        await loginAsAdmin({
          username: values.username!,
          password: values.password!,
        });
        onSuccess?.('admin');
        return;
      }

      if (loginMethod === 'username') {
        await login(values.username!, values.password!);
      } else {
        await loginByEmail(values.email!, values.code!);
      }
      onSuccess?.(loginMethod, loginTargetRef.current);
    } catch {
      // Error is handled by the store
    } finally {
      loginTargetRef.current = undefined;
    }
  };

  const isCodeLogin = loginMethod === 'email';
  const isAdminLogin = loginMethod === 'admin';
  const isPasswordLogin = loginMethod === 'username' || isAdminLogin;
  const activeError = isAdminLogin ? adminError : error;
  const localizedActiveError = (() => {
    if (!activeError) return null;
    if (
      activeError.includes('fetch')
      || activeError.includes('Network')
      || activeError.includes('网络连接失败')
    ) {
      return t('auth.networkError');
    }
    if (
      activeError.includes('401')
      || activeError.includes('密码')
      || activeError.toLowerCase().includes('invalid credentials')
    ) {
      return t('auth.invalidCredentials');
    }
    if (activeError.includes('429') || activeError.includes('过于频繁')) {
      return t('auth.loginError');
    }
    return activeError;
  })();
  const submitting = isAdminLogin ? isAdminLoading : isLoading;

  return (
    <div className="auth-container">
      <div className="auth-card auth-card--login">
        <TestVersionNotice variant="auth" />
        <div className="logo-section">
          <div className="hexagon-logo">
            <img src="/images/GH-logo.png" alt="GH Logo" />
          </div>
          <h1 className="brand-text">WaveLink</h1>
        </div>

        <div className="auth-method-switcher">
          {LOGIN_METHODS.map(({ key, labelKey }) => (
            <button
              key={key}
              type="button"
              className={`auth-method-switcher__btn${loginMethod === key ? ' auth-method-switcher__btn--active' : ''}`}
              onClick={() => handleMethodChange(key)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {localizedActiveError && (
          <div className="error-message">
            <Alert
              message={t('auth.loginFailed')}
              description={localizedActiveError}
              type="error"
              showIcon
              closable
              onClose={isAdminLogin ? clearAdminError : clearError}
            />
          </div>
        )}

        {sendCodeError && (
          <div className="error-message">
            <Alert
              message={t('auth.sendCodeFailed')}
              description={sendCodeError}
              type="error"
              showIcon
              closable
              onClose={() => setSendCodeError(null)}
            />
          </div>
        )}

        {codeInfoMessage && isCodeLogin && (
          <div className="success-message">
            <Alert
              message={t('auth.codeSent')}
              description={codeInfoMessage}
              type="info"
              showIcon
              closable
              onClose={() => setCodeInfoMessage(null)}
            />
          </div>
        )}

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="auth-form"
          initialValues={{ agreedToTerms: true }}
        >
          {(loginMethod === 'username' || isAdminLogin) && (
            <Form.Item
              name="username"
              rules={[{ required: true, message: t('auth.usernameRequired') }]}
              className="form-item"
            >
              <Input
                prefix={<UserOutlined className="input-icon" />}
                placeholder={isAdminLogin ? t('auth.adminUsername') : t('auth.username')}
                className="auth-input"
                size="large"
                autoComplete="username"
              />
            </Form.Item>
          )}

          {loginMethod === 'email' && (
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('auth.emailRequired') },
                { type: 'email', message: t('auth.emailInvalid') },
              ]}
              className="form-item"
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder={t('auth.registeredEmail')}
                className="auth-input"
                size="large"
                autoComplete="email"
              />
            </Form.Item>
          )}

          {isPasswordLogin && (
            <>
              <Form.Item
                name="password"
                rules={[{ required: true, message: t('auth.passwordRequired') }]}
                className="form-item"
              >
                <Input.Password
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder={t('auth.password')}
                  className="auth-input"
                  size="large"
                  autoComplete="current-password"
                />
              </Form.Item>

              {loginMethod === 'username' && (
                <div className="auth-forgot-row">
                  <button
                    type="button"
                    className="link-button auth-forgot-link"
                    onClick={onForgotPassword}
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              )}
            </>
          )}

          {isCodeLogin && (
            <>
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: t('auth.codeRequired') },
                  { pattern: /^\d{6}$/, message: t('auth.codeInvalid') },
                ]}
                className="form-item"
              >
                <Input
                  prefix={<SafetyCertificateOutlined className="input-icon" />}
                  placeholder={t('auth.codePlaceholder')}
                  className="auth-input"
                  size="large"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </Form.Item>

              <Form.Item className="form-item">
                <Button
                  type="default"
                  className="auth-button auth-button--secondary"
                  size="large"
                  loading={sendCodeLoading}
                  disabled={cooldown > 0}
                  onClick={() => void sendVerificationCode()}
                  block
                >
                  {cooldown > 0
                    ? t('auth.resendAfter', { seconds: cooldown })
                    : t('auth.sendCode')}
                </Button>
              </Form.Item>
            </>
          )}

          {!isAdminLogin && (
            <Form.Item
              name="agreedToTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('auth.termsRequired'))),
                },
              ]}
              className="form-item terms-checkbox-item"
            >
              <Checkbox>
                {t('auth.termsPrefix')}
                <button
                  type="button"
                  className="terms-link-inline"
                  onClick={(e) => {
                    e.preventDefault();
                    setTermsModalVisible(true);
                  }}
                >
                  {t('auth.terms')}
                </button>
                {' '}{t('auth.termsAnd')}{' '}
                <button
                  type="button"
                  className="terms-link-inline"
                  onClick={(e) => {
                    e.preventDefault();
                    setPrivacyModalVisible(true);
                  }}
                >
                  {t('auth.privacy')}
                </button>
              </Checkbox>
            </Form.Item>
          )}

          <Form.Item className="form-item">
            {isAdminLogin ? (
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="auth-button"
                size="large"
              >
                {submitting ? t('auth.loggingIn') : t('auth.enterAdmin')}
              </Button>
            ) : (
              <div className="auth-login-targets">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="auth-button"
                  size="large"
                  onClick={() => {
                    loginTargetRef.current = 'mobile';
                  }}
                >
                  {t('auth.mobileLogin')}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="auth-button"
                  size="large"
                  onClick={() => {
                    loginTargetRef.current = 'pc';
                  }}
                >
                  {t('auth.desktopLogin')}
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>

        {!isAdminLogin && (
          <div className="auth-links">
            <span className="link-text">{t('auth.noAccount')}</span>
            <button type="button" className="link-button" onClick={onSwitchToRegister}>
              {t('auth.registerNow')}
            </button>
          </div>
        )}
      </div>

      <TermsModal
        visible={termsModalVisible}
        type="terms"
        onClose={() => setTermsModalVisible(false)}
      />
      <TermsModal
        visible={privacyModalVisible}
        type="privacy"
        onClose={() => setPrivacyModalVisible(false)}
      />
    </div>
  );
};
